import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaUserCircle,
  FaUser,
  FaEdit,
  FaCog,
  FaQuestionCircle,
  FaSignOutAlt,
  FaKey,
  FaTachometerAlt,
  FaChevronDown,
} from "react-icons/fa";
import StorageService from "../services/StorageService";
import GlobalHeaderSearch from "./GlobalHeaderSearch";
import { 
  getAvailableRoles, 
  getCurrentRole, 
  setCurrentRole, 
  getProfileRouteByRole,
  getViewProfileRouteByRole 
} from "../utils/roleSwitcher";
import { getDashboardRoute } from "../utils/getDashboardRoute";
import "../assets/styles/common-header-footer.scss";
import PlatformNotificationBell from "./PlatformNotificationBell";
import usePlatformLogo from "../hooks/usePlatformLogo";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const platformLogoUrl = usePlatformLogo();

  // ---- auth + user ----
  const [user, setUser] = useState(() => StorageService.getUser());
  const [isAuthed, setIsAuthed] = useState(() => StorageService.isAuthenticated());

  // keep header in sync if another tab logs in/out or user info updated
  useEffect(() => {
    const onStorage = () => {
      setUser(StorageService.getUser());
      setIsAuthed(StorageService.isAuthenticated());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ---- custom profile menu (no Bootstrap dropdown) ----
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // ---- role switcher ----
  const [roleSwitcherOpen, setRoleSwitcherOpen] = useState(false);
  const roleSwitcherRef = useRef(null);
  const roleSwitcherPanelRef = useRef(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [currentRole, setCurrentRoleState] = useState(null);

  // Load available roles and current role
  useEffect(() => {
    if (isAuthed && user) {
      const roles = getAvailableRoles();
      setAvailableRoles(roles);
      const role = getCurrentRole();
      setCurrentRoleState(role);
      
      // If no role is set but user has roles, set the default
      if (!role && roles.length > 0) {
        // Default priority: Seller > Buyer > Quality Inspector
        const seller = roles.find(r => r.id === 2);
        const buyer = roles.find(r => r.id === 1);
        const defaultRole = seller || buyer || roles[0];
        if (defaultRole) {
          setCurrentRole(defaultRole, true);
          setCurrentRoleState(defaultRole);
        }
      }
    } else {
      setAvailableRoles([]);
      setCurrentRoleState(null);
    }
  }, [isAuthed, user]);

  // Listen for role changes from other tabs
  useEffect(() => {
    const handleStorageChange = () => {
      if (isAuthed && user) {
        const role = getCurrentRole();
        setCurrentRoleState(role);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthed, user]);

  // close on route change
  useEffect(() => {
    setOpen(false);
    setRoleSwitcherOpen(false);
  }, [location.pathname]);

  // Helper function to check if user is a seller
  const isSeller = () => {
    if (!user) return false;
    
    // Check user_type_id: 1 = BUYER, 2 = SELLER, 3 = BOTH
    let userTypeId = user.user_type_id;
    
    // Handle different formats: number, string, null, undefined
    if (userTypeId === null || userTypeId === undefined) {
      userTypeId = "";
    } else {
      userTypeId = String(userTypeId).trim();
    }
    
    // Convert to array of IDs (handle comma-separated or single value)
    const typeIds = userTypeId 
      ? userTypeId.split(",").map(id => String(id).trim()).filter(id => id && id !== "null" && id !== "undefined")
      : [];
    
    // Check if user is a seller (user_type_id = 2 or 3)
    const singleId = parseInt(userTypeId);
    const isSellerById = (!isNaN(singleId) && userTypeId.indexOf(",") === -1 && (singleId === 2 || singleId === 3)) ||
                         typeIds.some(id => {
                           const numId = parseInt(id);
                           return id === "2" || id === "3" || numId === 2 || numId === 3;
                         });
    
    return isSellerById;
  };

  // Helper function to check if user is a quality inspector
  const isQualityInspector = () => {
    if (!user) return false;
    
    // Check user_type_id: 4 = QUALITY_INSPECTOR
    const userTypeId = user.user_type_id;
    
    // Handle different formats: number, string, null, undefined
    if (userTypeId === null || userTypeId === undefined) {
      return false;
    }
    
    const typeId = parseInt(userTypeId);
    return !isNaN(typeId) && typeId === 4;
  };

  // close on click outside
  useEffect(() => {
    function handleClick(e) {
      if (open) {
        if (
          btnRef.current &&
          !btnRef.current.contains(e.target) &&
          panelRef.current &&
          !panelRef.current.contains(e.target)
        ) {
          setOpen(false);
        }
      }
      if (roleSwitcherOpen) {
        if (
          roleSwitcherRef.current &&
          !roleSwitcherRef.current.contains(e.target) &&
          roleSwitcherPanelRef.current &&
          !roleSwitcherPanelRef.current.contains(e.target)
        ) {
          setRoleSwitcherOpen(false);
        }
      }
    }
    function handleEsc(e) {
      if (e.key === "Escape") {
        setOpen(false);
        setRoleSwitcherOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, roleSwitcherOpen]);

  const handleLogout = () => {
    StorageService.clearAuth();
    setUser(null);
    setIsAuthed(false);
    navigate("/login");
  };

  // Get dashboard route based on current role
  const getDashboardRouteForHeader = () => {
    if (!isAuthed || !user) return "/login";
    return getDashboardRoute();
  };

  // Handle role switch
  const handleRoleSwitch = (role) => {
    setCurrentRole(role, true); // Remember the choice
    setCurrentRoleState(role);
    setRoleSwitcherOpen(false);
    setOpen(false); // Close profile menu as well
    
    // Navigate to the appropriate dashboard/profile based on role
    if (role.route) {
      navigate(role.route);
    }
  };

  return (
    <header className="common-header">
      <div className="common-header-container">
        {/* Logo */}
        <div className="common-header-logo">
          <Link to="/home" className="common-header-logo-link">
            <img 
              src={platformLogoUrl}
              alt="Trocanet Logo" 
              className="common-header-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="common-header-logo-text">Trocanet</span>
          </Link>
        </div>

        {/* Search */}
        <GlobalHeaderSearch variant="common" />

        {/* Role Switcher - Only show if user has multiple roles */}
        {isAuthed && availableRoles.length > 1 && (
          <div className="common-header-role-switcher" ref={roleSwitcherRef}>
            <button
              type="button"
              className="common-header-role-switcher-trigger"
              onClick={() => {
                setRoleSwitcherOpen(!roleSwitcherOpen);
              }}
              title="Switch Role"
            >
              <span className="common-header-role-switcher-text">
                {currentRole ? currentRole.name : (availableRoles[0]?.name || 'Select Role')}
              </span>
              <FaChevronDown 
                size={12} 
                className={`common-header-role-switcher-icon ${roleSwitcherOpen ? 'open' : ''}`} 
              />
            </button>
            
            {roleSwitcherOpen && (
              <div ref={roleSwitcherPanelRef} className="common-header-role-switcher-panel">
                <div className="common-header-role-switcher-header">Switch Role</div>
                <ul className="common-header-role-switcher-list">
                  {availableRoles.map((role) => (
                    <li key={role.id}>
                      <button
                        type="button"
                        className={`common-header-role-switcher-item ${
                          currentRole && currentRole.id === role.id ? 'active' : ''
                        }`}
                        onClick={() => handleRoleSwitch(role)}
                      >
                        <span>{role.name}</span>
                        {currentRole && currentRole.id === role.id && (
                          <span className="common-header-role-switcher-check">✓</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Dashboard Navigation - Only for authenticated users */}
        {isAuthed && (
          <>
          <div className="common-header-nav">
            <Link
              to={getDashboardRouteForHeader()}
              className="common-header-nav-link"
              title="Go to Dashboard"
            >
              <FaTachometerAlt className="common-header-nav-icon" />
              <span className="common-header-nav-text">Dashboard</span>
            </Link>
          </div>
          {/* Auction link - Only for sellers */}
          {isSeller() && (
            <div className="common-header-nav">
              <Link
                to="/auction/create"
                className="common-header-nav-link"
                title="Post Auction Product"
              >
                <span className="common-header-nav-text">🛒 Auctions</span>
              </Link>
            </div>
          )}
          {/* QA Dashboard link - Only for quality inspectors */}
          {isQualityInspector() && (
            <div className="common-header-nav">
              <Link
                to="/auction/qa/dashboard"
                className="common-header-nav-link"
                title="Inspection Requests Dashboard"
              >
                <span className="common-header-nav-text">📋 Inspection Requests</span>
              </Link>
            </div>
          )}
          </>
        )}

        {isAuthed && <PlatformNotificationBell />}

        {/* Profile menu */}
        <div className="common-header-profile">
          <button
            ref={btnRef}
            type="button"
            className="common-header-profile-trigger"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <FaUserCircle size={28} className="common-header-profile-icon" />
            <span className="common-header-profile-name">
              {isAuthed ? user?.name || "User" : "Login"}
            </span>
            <span className={`common-header-profile-caret ${open ? "open" : ""}`} />
          </button>

          {open && (
            <div ref={panelRef} className="common-header-profile-panel" role="menu">
              {/* Header */}
              <div className="common-header-profile-header">
                <div className="common-header-profile-avatar">
                  <FaUser size={20} />
                </div>
                <div className="common-header-profile-user">
                  <div className="common-header-profile-user-name">
                    {user?.name || "Guest"}
                  </div>
                  <div className="common-header-profile-user-role">
                    {user?.email || "Welcome"}
                  </div>
                </div>
              </div>

              {/* Items */}
              <ul className="common-header-profile-list">
                {isAuthed ? (
                  <>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to={getViewProfileRouteByRole()}
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaUser className="common-header-profile-item-icon" />
                        <span>My Profile</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to={getProfileRouteByRole()}
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaEdit className="common-header-profile-item-icon" />
                        <span>Edit Profile</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to="/settings"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaCog className="common-header-profile-item-icon" />
                        <span>Settings</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to="/help"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaQuestionCircle className="common-header-profile-item-icon" />
                        <span>Help</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to="/change-password"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaKey className="common-header-profile-item-icon" />
                        <span>Change Password</span>
                      </Link>
                    </li>
                    <li className="common-header-profile-divider" role="separator" />
                    <li>
                      <button
                        className="common-header-profile-item common-header-profile-item-danger"
                        onClick={handleLogout}
                        role="menuitem"
                      >
                        <FaSignOutAlt className="common-header-profile-item-icon" />
                        <span>Logout</span>
                      </button>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to="/login"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaUser className="common-header-profile-item-icon" />
                        <span>Login</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        className="common-header-profile-item"
                        to="/signup"
                        role="menuitem"
                        onClick={() => setOpen(false)}
                      >
                        <FaEdit className="common-header-profile-item-icon" />
                        <span>Sign Up</span>
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

